import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { IOutrightTournament } from '@/interface/IOutright';
import { SportType } from '@/interface/enum';
import { remove, upsert } from '@/libraries/array';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import BusinessLogicHelper from '@/libraries/helpers/businessLogicHelper';
import { formatOutrightTournament } from '@/libraries/oddsApi/oddsApiFormatter';
import { sortOutrightTournaments } from '@/libraries/oddsDisplay';
import { useOddsApiStore } from '@/stores/oddsApiStore';
import { useRootStore } from '@/stores/rootStore';
import { useOutrightTournamentsQuery, useOutrightTournamentsSubscription } from '@/types';

type OddsQueryVariable = {
    sportType: MaybeRef<SportType>;
    leagueNames: MaybeRef<string[] | undefined>;
    leagueIds: MaybeRef<number[] | undefined>;
    isEnabled: MaybeRef<boolean>;
}

export function useOutrightTournaments(
    { isEnabled: _isEnabled, sportType, leagueNames, leagueIds }: OddsQueryVariable,
) {
    const outrightTournaments = ref<IOutrightTournament[]>([]);

    const variable = deepCompareComputed(() => ({
        query: {
            sport: SportType.toOddsApi(get(sportType)),
            tournamentNames: get(leagueNames),
            tournamentIds: get(leagueIds),
        },
    }));

    const { isWsConnected } = storeToRefs(useRootStore());

    function getPollInterval() {
        if (isWsConnected.value) {
            return undefined;
        }
        return 60000;
    }

    const { customerSettings } = storeToRefs(useOddsApiStore());
    const isEnabled = computed(() => {
        if (!BusinessLogicHelper.isAllowedSport(customerSettings.value, SportType.toOddsApi(get(sportType)))) {
            return false;
        }
        return get(_isEnabled);
    });

    const { onResult, refetch, loaded } = useOutrightTournamentsQuery(
        variable,
        deepCompareComputed(() => ({
            enabled: isEnabled.value,
            pollInterval: getPollInterval(),
        })),
    );

    onResult((data) => {
        if (!data) {
            return;
        }

        const formatted = data.map(e => formatOutrightTournament(e));
        outrightTournaments.value = sortOutrightTournaments(formatted);
    });

    const { onSubscription } = useOutrightTournamentsSubscription(
        variable,
        deepCompareComputed(() => ({
            enabled: isEnabled.value,
        })),
    );

    onSubscription((data) => {
        if (!data) {
            return;
        }

        const { updated, deleted } = data;

        upsert(outrightTournaments.value, updated
            .map(e => Object.freeze(formatOutrightTournament(e))), item => item.id);

        remove(outrightTournaments.value, deleted, (oldTournament, deletedTournament) => oldTournament.id === deletedTournament.id);

        outrightTournaments.value = sortOutrightTournaments(outrightTournaments.value);
    });

    watch(variable, () => {
        outrightTournaments.value = [];
    });

    return {
        loaded,
        outrightTournaments,
        refetch,
    };
}
